import clsx from 'clsx';
import {
  type ElementType,
  type ReactElement,
  type ReactNode,
  forwardRef,
  useId,
  useMemo,
  Children,
  Fragment,
} from 'react';
import { isElement } from 'react-is';

import { _mergeProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { _textStyleCSS as textStyleCSS } from '@dynatrace/strato-components/styles';
import { Text } from '@dynatrace/strato-components/typography';

import * as styles from './BaseChip.sty.js';
import { ChipPrefix, ChipSuffix } from './ChipContent.js';
import { ChipDeleteButton } from './ChipDeleteButton.js';
import { ChipKey } from './ChipKey.js';
import { ChipContext } from './contexts/ChipContext.js';
import { useChipContext } from './contexts/useChipContext.js';
import type { ChipProps } from './types/chip.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import { fieldColorsCSS } from '../../styles/field.sty.js';

//#region Typings
type ChipSlots = {
  deleteButton: ReactNode | null;
  key: ReactNode[];
  value: ReactNode[];
  prefixIcons: ReactNode[];
  suffixIcons: ReactNode[];
};
//#endregion Typings

/** Iterates over child nodes to extract specific component slots. */
const getChipSlots = (node: ReactNode) => {
  const slots: ChipSlots = {
    deleteButton: null,
    prefixIcons: [],
    suffixIcons: [],
    value: [],
    key: [],
  };

  for (const child of Children.toArray(node)) {
    if (isElement(child)) {
      switch (child.type) {
        case ChipPrefix:
          slots.prefixIcons.push(child);
          break;
        case ChipSuffix:
          slots.suffixIcons.push(child);
          break;
        case ChipKey:
          slots.key.push(child);
          break;
        case ChipDeleteButton:
          slots.deleteButton = child;
          break;
        default:
          slots.value.push(child);
      }
    } else {
      slots.value.push(child);
    }
  }

  return slots;
};

/** Chip component */
const RootBaseChip: <E extends ElementType = 'div'>(
  props: ChipProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: ChipProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      as = 'div' as E,
      variant = 'emphasized',
      color = 'neutral',
      size = 'default',
      disabled = false,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      maxWidth,
      className: consumerClassName,
      style: consumerStyle = {},
      ...remainingProps
    }: ChipProps<E> = props;

    const { prefixIcons, suffixIcons, deleteButton, key, value } = useMemo(
      () => getChipSlots(children),
      [children],
    );

    const { focusProps, focusClassName } = useFocusRing({
      color,
      disabled,
    });

    const valueId = useId();
    const hasKey = key.length > 0;

    return (
      <Flex
        as="div"
        ref={forwardedRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        gap={2}
        className={clsx(
          styles.wrapper,
          disabled && styles.disabledWrapper,
          fieldColorsCSS({ variant, color, disabled }),
          consumerClassName,
        )}
        style={{
          maxWidth: maxWidth ?? 250,
          ...consumerStyle,
        }}
      >
        <ChipContext.Provider
          value={{ disabled, variant, color, size, valueId, hasKey }}
        >
          <Text
            as={as satisfies ElementType}
            textStyle="base"
            className={clsx(
              'strato-surface',
              focusClassName,
              (as === 'a' || as === 'button') &&
                textStyleCSS({ textStyle: 'base-emphasized' }),
              fieldColorsCSS({ variant, color, disabled }),
              styles.container({
                size,
                disabled,
                color,
                variant,
                hasDeleteButton: deleteButton !== null,
              }),
            )}
            size={size}
            disabled={disabled}
            variant={variant}
            color={color}
            {..._mergeProps(
              focusProps,
              remainingProps as Record<string, unknown>,
            )}
          >
            {prefixIcons.map((node: ReactNode, index: number) => (
              <Fragment key={`prefix-node-${index}`}>{node}</Fragment>
            ))}
            <TextContainer keys={key} values={value} />
            {suffixIcons.map((node, index) => (
              <Fragment key={`suffix-node-${index}`}>{node}</Fragment>
            ))}
          </Text>
          {deleteButton}
        </ChipContext.Provider>
      </Flex>
    );
  },
);

function TextContainer({
  keys,
  values,
}: {
  keys: ReactNode[];
  values: ReactNode[];
}) {
  const { valueId: id } = useChipContext();
  const hasValue = values.length > 0;
  const hasKey = keys.length > 0;

  if (!(hasKey || hasValue)) {
    return null;
  }

  const separator = hasKey && hasValue ? <span>:</span> : null;

  return (
    <div className={styles.textContainer}>
      {keys.map((node: ReactNode, index: number) => (
        <Fragment key={`key-node-${index}`}>{node}</Fragment>
      ))}
      {separator}
      {values.map((node, index) => (
        <Flex key={`value-node-${index}`} id={id} className={styles.value}>
          {node}
        </Flex>
      ))}
    </div>
  );
}

/**
 * Use the `Chip` component to present a piece of information in a compact form.
 * @internal
 */
export const BaseChip = Object.assign(RootBaseChip, {
  Prefix: ChipPrefix,
  Suffix: ChipSuffix,
  Key: ChipKey,
  DeleteButton: ChipDeleteButton,
});

(BaseChip as typeof BaseChip & { displayName: string }).displayName =
  'BaseChip';
